import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Auth pages
import Login from './pages/auth/Login';
import ResetPassword from './pages/auth/ResetPassword';
import ChangePassword from './pages/auth/ChangePassword';


// Scholar pages
import ScholarDashboard from './pages/scholar/ScholarDashboard';
import MonthlyClaimForm from './pages/scholar/MonthlyClaimForm';
import Publications from './pages/scholar/Publications';
import UpdateBankDetails from './pages/scholar/UpdateBankDetails';


// Common pages
import TRFGuidelines from './pages/common/TRFGuidelines';
import LandingPage from './pages/public/LandingPage';
import VerifyClaim from './pages/public/VerifyClaim';

// Supervisor pages
import SupervisorDashboard from './pages/supervisor/SupervisorDashboard';
import SupervisorClaimReview from './pages/supervisor/SupervisorClaimReview';

// DLC pages
import DLCDashboard from './pages/dlc/DLCDashboard';
import DLCClaimReview from './pages/dlc/DLCClaimReview';

// HOD pages
import HODDashboard from './pages/hod/HODDashboard';
import HODClaimReview from './pages/hod/HODClaimReview';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import EnrollScholar from './pages/admin/EnrollScholar';
import EnrollStaff from './pages/admin/EnrollStaff';
import ManageClaims from './pages/admin/ManageClaims';
import AdminClaimReview from './pages/admin/AdminClaimReview';
import UserManagement from './pages/admin/UserManagement';
import AdminReports from './pages/admin/AdminReports';

import './styles/index.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading, mustChangePassword } = useAuth();


    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Redirect to change password if required (skip for the change-password route itself)
    if (mustChangePassword && window.location.pathname !== '/change-password') {
        return <Navigate to="/change-password" replace />;
    }

    // Redirect to change password if required (skip for the change-password route itself)
    if (mustChangePassword && window.location.pathname !== '/change-password') {
        return <Navigate to="/change-password" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user.role)) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Dashboard Router based on role
const DashboardRouter = () => {
    const { user } = useAuth();

    if (!user) return <Navigate to="/home" replace />;

    switch (user.role) {
        case 'scholar':
            return <Navigate to="/scholar/dashboard" replace />;
        case 'supervisor':
            return <Navigate to="/supervisor/dashboard" replace />;
        case 'dlc':
            return <Navigate to="/dlc/dashboard" replace />;
        case 'hod':
            return <Navigate to="/hod/dashboard" replace />;
        case 'admin':
            return <Navigate to="/admin/dashboard" replace />;
        default:
            return <Navigate to="/home" replace />;
    }
};

function App() {
    return (
        <AuthProvider>
            <Router>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<Navigate to="/home" replace />} />
                    <Route path="/home" element={<LandingPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/change-password" element={<ChangePassword />} />
                    <Route path="/verifyclaim" element={<VerifyClaim />} />
                    <Route path="/verifyclaim/:email/:month/:year" element={<VerifyClaim />} />

                    {/* Dashboard Router */}
                    <Route path="/dashboard" element={<DashboardRouter />} />

                    {/* Scholar Routes */}
                    <Route
                        path="/scholar/dashboard"
                        element={
                            <ProtectedRoute allowedRoles={['scholar']}>
                                <ScholarDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/scholar/update-bank-details"
                        element={
                            <ProtectedRoute allowedRoles={['scholar']}>
                                <UpdateBankDetails />
                            </ProtectedRoute>
                        }
                    />
                    
                    <Route
                        path="/scholar/monthly-claim"
                        element={
                            <ProtectedRoute allowedRoles={['scholar']}>
                                <MonthlyClaimForm />
                            </ProtectedRoute>
                        }
                    />
                    
                    <Route
                        path="/scholar/publications"
                        element={
                            <ProtectedRoute allowedRoles={['scholar']}>
                                <Publications />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/trf-guidelines"
                        element={
                            <ProtectedRoute allowedRoles={['scholar', 'supervisor', 'dlc', 'hod', 'admin']}>
                                <TRFGuidelines />
                            </ProtectedRoute>
                        }
                    />

                    {/* Supervisor Routes */}
                    <Route
                        path="/supervisor/dashboard"
                        element={
                            <ProtectedRoute allowedRoles={['supervisor']}>
                                <SupervisorDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/supervisor/claim/:id"
                        element={
                            <ProtectedRoute allowedRoles={['supervisor']}>
                                <SupervisorClaimReview />
                            </ProtectedRoute>
                        }
                    />

                    {/* DLC Routes */}
                    <Route
                        path="/dlc/dashboard"
                        element={
                            <ProtectedRoute allowedRoles={['dlc']}>
                                <DLCDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dlc/claim/:id"
                        element={
                            <ProtectedRoute allowedRoles={['dlc']}>
                                <DLCClaimReview />
                            </ProtectedRoute>
                        }
                    />

                    {/* HOD Routes */}
                    <Route
                        path="/hod/dashboard"
                        element={
                            <ProtectedRoute allowedRoles={['hod']}>
                                <HODDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/hod/claim/:id"
                        element={
                            <ProtectedRoute allowedRoles={['hod']}>
                                <HODClaimReview />
                            </ProtectedRoute>
                        }
                    />

                    {/* Admin Routes */}
                    <Route
                        path="/admin/dashboard"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <AdminDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/enroll"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <EnrollScholar />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/enroll-supervisor"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <EnrollStaff />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/enroll-staff"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <EnrollStaff />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/manage-claims"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <ManageClaims />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/claim/:id"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <AdminClaimReview />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/users"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <UserManagement />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/reports"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <AdminReports />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/reports"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <AdminReports />
                            </ProtectedRoute>
                        }
                    />

                    {/* 404 */}
                    <Route path="*" element={<div>404 - Page Not Found</div>} />
                </Routes>
            </Router>
        </AuthProvider>
    );
}

export default App;
